type ErrorReport = {
  contexts?: Record<string, Record<string, boolean | number | string | undefined>>
  err: Error
  fingerprint?: string[]
  tags?: Record<string, string>
}

type ErrorClient = {
  captureException: (err: Error, details?: Omit<ErrorReport, 'err'>) => void
  setUser: (user: { email: string } | null) => void
}

const reports: ErrorReport[] = []
const maxReports = 50
let client: ErrorClient | undefined
let pendingEmail: string | undefined
let installed = false

// NOTE(Rok Kos): this is to improve first time load and lazy loading.
// Queue early errors without loading the Sentry SDK before the first paint.
const isEnabled = () => {
  if (window.location.hostname === 'localhost') return false
  try {
    return window.self === window.top
  } catch {
    return false
  }
}

const toError = (value: unknown) => {
  if (value instanceof Error) return value
  if (typeof value === 'string') return new Error(value)
  return new Error(String(value))
}

const onError = (event: ErrorEvent) => {
  if (event.target instanceof HTMLElement) return
  captureError(event.error ?? event.message, { tags: { error_source: 'early_global' } })
}
const onUnhandledRejection = (event: PromiseRejectionEvent) => captureError(event.reason, { tags: { error_source: 'early_unhandled_rejection' } })

export const installEarlyErrorMonitoring = () => {
  if (installed || !isEnabled()) return
  installed = true
  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onUnhandledRejection)
}

export const captureError = (value: unknown, details: Omit<ErrorReport, 'err'> = {}) => {
  if (!isEnabled()) return
  const report = { ...details, err: toError(value) }
  if (client) {
    client.captureException(report.err, details)
    return
  }
  reports.push(report)
  if (reports.length > maxReports) reports.shift()
}

export const setErrorUser = (email: string) => {
  if (!isEnabled()) return
  pendingEmail = email
  client?.setUser({ email })
}

export const connectErrorMonitoring = (connectedClient: ErrorClient) => {
  client = connectedClient
  window.removeEventListener('error', onError)
  window.removeEventListener('unhandledrejection', onUnhandledRejection)
  if (pendingEmail) client.setUser({ email: pendingEmail })
  for (const report of reports.splice(0)) {
    const { err, ...details } = report
    client.captureException(err, details)
  }
}
